import OneLinkSmartScript from '@Analytics/OneLinkSmartScript';
import { SWRConfig } from 'swr';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import SEO from '@seo/index';
import {
  fetchEducatorLevelDisplayConfig,
  fetchUserProfileData,
  fetchUserProfileStats
} from '@api/fetchers/profile';
import escape from '@utils/escapeHTML';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import EducatorPageLayout from '@layout/EducatorPageLayout/EducatorPageLayout';
import EducatorTabs from '@cont/EducatorProfile/EducatorTabs';
import { useFullProfileInfoNew } from '@api/hooks/profile/useFullProfileInfo';
import { COURSE_TAB_INDEX } from '@cont/EducatorProfile/constants';
import { EDUCATOR_COURSES } from '@constants/segment';
import useProfileFeed from '@api/hooks/profile/useProfileFeed';
import { fetchEducatorCourses } from '@api/fetchers/courses';
import CoursesList from '@cont/EducatorProfile/Courses';

const CoursesPage = ({
  profileInfo,
  fallbackData,
  fallbackCourses,
  filtersAppliedOnServer,
  userName,
  authorUID
}) => {
  const router = useRouter();
  let { authorID } = router.query;
  if (!authorID.startsWith('@')) authorID = `@${authorID}`;
  authorID = escape(authorID);

  const { profileFeed } = useProfileFeed(userName);
  const { firstName, lastName, description, isExEducator } = profileInfo;

  const title = `${firstName} ${lastName} courses | Unacademy`;
  const url = `https://unacademy.com/${authorID}/courses`;

  const { keys } = useFullProfileInfoNew({
    username: userName,
    fallbackData,
    authorUID
  });

  return (
    <SWRConfig
      value={{
        fallback: {
          [keys.profileDataKey]: fallbackData.profileData,
          [keys.profileStatsKey]: fallbackData.profileStats,
          [keys.levelDisplayInfoKey]: fallbackData.levelDisplayInfo,
          [keys.learnerMileStonesInfokey]: fallbackData.learnerMileStonesInfo,
          [`sheldon/v1/list/course?username=${userName.toLowerCase()}&limit=12&offset=0&type=popular`]:
            fallbackCourses
        }
      }}
    >
      <SEO
        title={title}
        url={url}
        description={description}
        openGraph={{ title, description, url }}
      />
      <EducatorTabs
        tabIndex={COURSE_TAB_INDEX}
        isExEducator={isExEducator}
        dataInfo={profileFeed}
      />
      <CoursesList
        authorID={authorID}
        filtersApplied={filtersAppliedOnServer}
        fallbackCourses={fallbackCourses}
      />
      <OneLinkSmartScript />
    </SWRConfig>
  );
};

CoursesPage.pageInfo = {
  name: EDUCATOR_COURSES,
  layoutOptions: {
    hideFooter: true
  },
  isMWebSupported: true
};

export const getStaticProps = async ({ params: query }) => {
  let userName = query.authorID;
  if (!userName) return { notFound: true };
  if (userName.startsWith('@')) userName = query.authorID?.slice(1);
  userName = escape(userName);

  const profileInfo = await fetchUserProfileData(userName);
  if (!profileInfo || profileInfo.errorCode)
    return {
      notFound: true,
      revalidate: 5 // keeping the revalidate time to 5 seconds as this page is cached over cdn
    };

  const { isEducator, isExEducator, isModerator } = profileInfo;
  const isEducatorProfile = isEducator || isExEducator || isModerator;

  // if user is Learner we should redirect to learn page instead of showing page not found
  const isLearnerProfile = !isEducator && !isExEducator && !isModerator;

  if (isLearnerProfile)
    return {
      redirect: { destination: `/learner/@${userName}`, permanent: true }
    };

  if (!isEducatorProfile)
    return {
      notFound: true,
      revalidate: 5 // keeping the revalidate time to 5 seconds as this page is cached over cdn
    };

  const initialPromises = [
    fetchUserProfileStats(userName),
    fetchEducatorLevelDisplayConfig()
  ];
  const [profileStats, levelDisplayInfo] = await Promise.all(initialPromises);

  /** Course Data */
  const filtersToApply = {
    limit: 12,
    offset: 0,
    type: 'popular'
  };
  let fallbackCourses = await fetchEducatorCourses(userName, filtersToApply);

  if (!fallbackCourses?.results?.length) {
    fallbackCourses = { results: [] };
  }

  return {
    props: {
      userName,
      profileInfo,
      fallbackCourses,
      filtersAppliedOnServer: filtersToApply,
      authorUID: profileInfo.uid,
      fallbackData: {
        profileData: profileInfo,
        profileStats,
        levelDisplayInfo
      },
      globalFallbackData: {
        [`sheldon/v1/user/info?username=${userName.toLowerCase()}`]:
          profileInfo,
        [`v1/user/${userName}/stats/`]: profileStats
      }
    },
    revalidate: 5 // keeping the revalidate time to 5 seconds as this page is cached over cdn
  };
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

export default CoursesPage;

CoursesPage.propTypes = {
  profileInfo: PropTypes.objectOf(PropTypes.any).isRequired,
  fallbackCourses: PropTypes.objectOf(PropTypes.any).isRequired,
  filtersAppliedOnServer: PropTypes.objectOf(PropTypes.any).isRequired
};
CoursesPage.Layout = EducatorPageLayout;
CoursesPage.Wrapper = PageReduxProvider;
